<template>
  <ChatCard ref="chatCard" :channel="chatChannel" :key="chatChannel" class="mt-3 chat-card-block mb-4" id="chat" />
</template>
<script setup lang="ts">
import ChatCard from '@/components/chat/ChatCard.vue';
import { computed, ref, watch } from 'vue';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import {
  createOrderSupportFunctions,
  orderSupportWorkflowGuide,
  toAutopilotOrderSupport,
} from '@/autopilot/functions/orders/orderSupport';

const props = defineProps<{ orderId: string }>();

/* ---------------------------------- STATE --------------------------------- */
const chatCard = ref<InstanceType<typeof ChatCard> | null>(null);

/* -------------------------------- COMPUTED -------------------------------- */
const chatChannel = computed(() => `job:${props.orderId}.public`);

/* -------------------------------- AUTOPILOT ------------------------------- */
const routeReady = useAutopilotRouteReady();
useAutopilotContext('order_support_workflow', () => orderSupportWorkflowGuide);
useAutopilotContext('order_support', () =>
  toAutopilotOrderSupport(props.orderId, chatCard.value?.messages ?? [], chatCard.value?.currentMessage ?? '')
);

useAutopilotFunctions(
  createOrderSupportFunctions({
    getDraft: () => chatCard.value?.currentMessage ?? '',
    setDraft: (text) => chatCard.value?.setDraft(text),
    send: () => chatCard.value?.sendMessage() ?? Promise.reject(new Error('The support chat is not loaded.')),
    isSending: () => !!chatCard.value?.isSending,
  })
);

/* -------------------------------- WATCHERS -------------------------------- */
// the route is ready once the chat's first fetch has finished and its messages are in context
watch(
  () => chatCard.value?.isLoading,
  (isLoading, wasLoading) => {
    if (wasLoading && !isLoading) routeReady();
  }
);
</script>
<style scoped>
.chat-card-block :deep(.chat) {
  max-height: 400px;
  overflow-y: auto;
}
</style>
